import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import supabase from '@/lib/supabase';

type Provider = 'meta_cloud' | 'evolution';

interface EvolutionFields {
  server_url: string;
  api_key: string;
  instance_name: string;
}

interface MetaFields {
  phone_number_id: string;
  access_token: string;
  business_account_id: string;
  webhook_verify_token: string;
  api_version: string;
}

interface InstanceRow {
  id: string;
  company_id: string;
  instance_name: string;
  instance_id: string | null;
  server_url: string;
  api_key: string;
  phone_number: string | null;
  status: string;
  qrcode_base64: string | null;
  created_at: string;
  updated_at: string;
}

const instanceStatusMap: Record<string, { label: string; color: string; dot: string }> = {
  connected: { label: 'Conectado', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  disconnected: { label: 'Desconectado', color: 'bg-background-100 text-foreground-500 border-background-200', dot: 'bg-foreground-400' },
  connecting: { label: 'Conectando', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500 animate-pulse' },
  qr_ready: { label: 'QR Code pronto', color: 'bg-accent-50 text-accent-700 border-accent-200', dot: 'bg-accent-500' },
};

export default function SettingsWhatsAppPage() {
  const { profile } = useAuth();

  const [provider, setProvider] = useState<Provider>('meta_cloud');

  const [meta, setMeta] = useState<MetaFields>({
    phone_number_id: '',
    access_token: '',
    business_account_id: '',
    webhook_verify_token: '',
    api_version: 'v22.0',
  });

  const [evolution, setEvolution] = useState<EvolutionFields>({
    server_url: '',
    api_key: '',
    instance_name: '',
  });
  const [rateLimit, setRateLimit] = useState(30);

  const [showMetaToken, setShowMetaToken] = useState(false);
  const [showEvolutionKey, setShowEvolutionKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  // Multi-instance state
  const [instances, setInstances] = useState<InstanceRow[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [newInstanceName, setNewInstanceName] = useState('');
  const [creatingInstance, setCreatingInstance] = useState(false);
  const [deletingInstance, setDeletingInstance] = useState<string | null>(null);
  const [showQR, setShowQR] = useState<string | null>(null);

  // Test message state
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Olá! Esta é uma mensagem de teste do WzapPro 🚀');
  const [sendingTest, setSendingTest] = useState(false);

  // Reconnect state
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectQrCode, setReconnectQrCode] = useState<string | null>(null);

  // Provisioning state (SaaS — uses admin's global server)
  const [provisioning, setProvisioning] = useState(false);
  const [provisionMessage, setProvisionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [provisionQrCode, setProvisionQrCode] = useState<string | null>(null);

  const fetchInstances = useCallback(async () => {
    if (!profile?.company_id || provider !== 'evolution') return;
    setLoadingInstances(true);
    const { data, error } = await supabase
      .from('evolution_instances')
      .select('*')
      .eq('company_id', profile.company_id)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setInstances(data as InstanceRow[]);
    }
    setLoadingInstances(false);
  }, [profile?.company_id, provider]);

  // Auto-check Evolution API instance connection state
  const checkEvolutionState = async (url: string, key: string, instance: string) => {
    try {
      const baseUrl = url.replace(/\/+$/, '');
      const stateRes = await fetch(`${baseUrl}/instance/connectionState/${instance}`, {
        headers: {
          'Content-Type': 'application/json',
          apikey: key,
        },
      });
      if (stateRes.ok) {
        const stateData = await stateRes.json();
        const isOpen = stateData?.instance?.state === 'open';
        setIsConnected(isOpen);
        // Also sync to DB if connected
        if (isOpen && profile?.company_id) {
          await supabase
            .from('evolution_instances')
            .update({ status: 'connected', updated_at: new Date().toISOString() })
            .eq('company_id', profile.company_id)
            .eq('instance_name', instance);
          await supabase
            .from('whatsapp_configs')
            .update({ is_connected: true, updated_at: new Date().toISOString() })
            .eq('company_id', profile.company_id);
        }
      }
    } catch {
      // Silently ignore auto-check failures
    }
  };

  // Load existing config - runs once per company_id (NOT tied to provider changes)
  useEffect(() => {
    if (!profile?.company_id) return;

    supabase
      .from('whatsapp_configs')
      .select('*')
      .eq('company_id', profile.company_id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error || !data) return;

        const savedProvider = (data.provider as Provider) || 'meta_cloud';
        setProvider(savedProvider);

        if (savedProvider === 'meta_cloud') {
          setMeta({
            phone_number_id: data.phone_number_id || '',
            access_token: data.access_token || '',
            business_account_id: data.business_account_id || '',
            webhook_verify_token: data.webhook_verify_token || '',
            api_version: data.api_version || 'v22.0',
          });
        } else {
          setEvolution({
            server_url: data.evolution_server_url || '',
            api_key: data.evolution_api_key || '',
            instance_name: data.evolution_instance_name || '',
          });
        }

        setRateLimit(data.rate_limit_per_minute || 30);

        setIsConnected(!!data.is_connected);

        // Auto-check connection for Evolution provider
        if (savedProvider === 'evolution' && data.evolution_server_url && data.evolution_api_key && data.evolution_instance_name) {
          checkEvolutionState(data.evolution_server_url, data.evolution_api_key, data.evolution_instance_name);
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.company_id]);

  // Fetch evolution instances separately - re-runs when provider or company changes
  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  // Polling: auto-refresh instance status every 3 seconds AND actively check Evolution server state
  useEffect(() => {
    if (provider !== 'evolution' || instances.length === 0) return;

    const firstInstance = instances[0];
    // Only actively poll Evolution server when not yet connected
    if (firstInstance.status === 'connected') return;

    const interval = setInterval(async () => {
      // 1. Refresh from DB
      await fetchInstances();

      // 2. Actively check Evolution server connection state
      const { data: instData } = await supabase
        .from('evolution_instances')
        .select('server_url, api_key, instance_name')
        .eq('company_id', profile?.company_id)
        .eq('instance_name', firstInstance.instance_name)
        .maybeSingle();

      if (!instData?.server_url || !instData?.api_key) return;

      try {
        const baseUrl = instData.server_url.replace(/\/+$/, '');
        const stateRes = await fetch(`${baseUrl}/instance/connectionState/${instData.instance_name}`, {
          headers: {
            'Content-Type': 'application/json',
            apikey: instData.api_key,
          },
        });

        if (stateRes.ok) {
          const stateData = await stateRes.json();
          const serverState = stateData?.instance?.state;

          if (serverState === 'open') {
            // Instance is connected on the server! Update DB immediately
            await supabase
              .from('evolution_instances')
              .update({ status: 'connected', qrcode_base64: null, updated_at: new Date().toISOString() })
              .eq('company_id', profile?.company_id)
              .eq('instance_name', instData.instance_name);
            await supabase
              .from('whatsapp_configs')
              .update({ is_connected: true, updated_at: new Date().toISOString() })
              .eq('company_id', profile?.company_id);
            setIsConnected(true);
            setProvisionQrCode(null);
            setReconnectQrCode(null);
            await fetchInstances();
          } else if (serverState === 'connecting') {
            await supabase
              .from('evolution_instances')
              .update({ status: 'connecting', updated_at: new Date().toISOString() })
              .eq('company_id', profile?.company_id)
              .eq('instance_name', instData.instance_name);
            await fetchInstances();
          } else if (serverState === 'close' || serverState === 'closed') {
            // Connection dropped! Update status and try to auto-restore
            await supabase
              .from('evolution_instances')
              .update({ status: 'disconnected', updated_at: new Date().toISOString() })
              .eq('company_id', profile?.company_id)
              .eq('instance_name', instData.instance_name);
            await supabase
              .from('whatsapp_configs')
              .update({ is_connected: false, updated_at: new Date().toISOString() })
              .eq('company_id', profile?.company_id);
            setIsConnected(false);
            await fetchInstances();

            // Auto-try to restart instance to restore session (without new QR)
            try {
              await fetch(`${baseUrl}/instance/restart/${instData.instance_name}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  apikey: instData.api_key,
                },
              });
            } catch {
              // restart might not be supported — that's ok
            }
          }
        }
      } catch {
        // Silently ignore — server might be temporarily unreachable
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [provider, instances, fetchInstances, profile?.company_id]);

  // Derive isConnected from the actual instance status in the database
  useEffect(() => {
    if (provider !== 'evolution' || instances.length === 0) return;
    const firstInstance = instances[0];
    const dbConnected = firstInstance.status === 'connected';
    setIsConnected(dbConnected);
  }, [instances, provider]);

  const isValid = provider === 'meta_cloud'
    ? !!(meta.phone_number_id && meta.access_token)
    : !!(evolution.server_url && evolution.api_key);

  const buildUpsertPayload = () => {
    const base = {
      company_id: profile?.company_id,
      provider,
      rate_limit_per_minute: rateLimit,
      updated_at: new Date().toISOString(),
    };

    if (provider === 'meta_cloud') {
      return {
        ...base,
        phone_number_id: meta.phone_number_id,
        access_token: meta.access_token,
        business_account_id: meta.business_account_id,
        webhook_verify_token: meta.webhook_verify_token,
        api_version: meta.api_version,
        evolution_server_url: null,
        evolution_api_key: null,
        evolution_instance_name: null,
      };
    }

    return {
      ...base,
      evolution_server_url: evolution.server_url,
      evolution_api_key: evolution.api_key,
      evolution_instance_name: evolution.instance_name,
      phone_number_id: null,
      access_token: null,
      business_account_id: null,
      webhook_verify_token: null,
      api_version: null,
    };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.company_id) return;
    setSaving(true);
    setSaved(false);

    const payload = buildUpsertPayload();
    const { error } = await supabase
      .from('whatsapp_configs')
      .upsert(payload, { onConflict: 'company_id' });

    setSaving(false);
    if (error) {
      setTestResult({ success: false, message: `Erro ao salvar: ${error.message}` });
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  const handleTest = async () => {
    if (!isValid) return;

    if (profile?.company_id) {
      const payload = buildUpsertPayload();
      await supabase.from('whatsapp_configs').upsert(payload, { onConflict: 'company_id' });
    }

    setTesting(true);
    setTestResult(null);

    const { data, error } = await supabase.functions.invoke('test-whatsapp-connection', {
      body: {},
    });

    setTesting(false);

    if (error || !data?.success) {
      setTestResult({
        success: false,
        message: data?.message || error?.message || 'Falha na conexão. Verifique as credenciais.',
      });
      setIsConnected(false);
    } else {
      if (provider === 'evolution') {
        setTestResult({
          success: true,
          message: `Conectado! Instância: ${data.instanceName || 'N/A'} — Status: ${data.state || 'N/A'}`,
        });
      } else {
        setTestResult({
          success: true,
          message: `Conectado! Número: ${data.phoneNumber || 'N/A'} — Qualidade: ${data.qualityRating || 'N/A'}`,
        });
      }
      setIsConnected(true);
    }
  };

  const handleProviderSwitch = (newProvider: Provider) => {
    setProvider(newProvider);
    setTestResult(null);
    setSaved(false);
  };

  // ─── SaaS Evolution: Auto-provision instance ───
  const handleProvision = async () => {
    setProvisioning(true);
    setProvisionMessage(null);
    setProvisionQrCode(null);

    try {
      const { data, error } = await supabase.functions.invoke('provision-evolution-instance', {
        body: {},
      });

      if (error) {
        setProvisionMessage({
          type: 'error' as const,
          text: error.message || 'Falha ao comunicar com o servidor. Tente novamente.',
        });
        setProvisioning(false);
        setTimeout(() => setProvisionMessage(null), 10000);
        return;
      }

      if (!data?.success) {
        setProvisionMessage({
          type: 'error' as const,
          text: data?.message || 'Falha ao criar instância. Tente novamente.',
        });
        setProvisioning(false);
        setTimeout(() => setProvisionMessage(null), 10000);
        return;
      }

      // Instance created or already exists — now generate QR Code
      const instanceName = data.instanceName || data?.instance?.instanceName || '';

      setProvisionMessage({
        type: 'success' as const,
        text: data?.alreadyExists
          ? 'Instância encontrada! Gerando QR Code...'
          : `Instância criada! Gerando QR Code...`,
      });

      // Refresh local state
      setEvolution((prev) => ({ ...prev, instance_name: instanceName }));
      await fetchInstances();

      if (instanceName) {
        await handleGenerateQR(instanceName);
      }

      setTimeout(() => setProvisionMessage(null), 8000);
    } catch (err: unknown) {
      setProvisionMessage({
        type: 'error' as const,
        text: err instanceof Error ? err.message : 'Erro inesperado ao criar instância.',
      });
      setTimeout(() => setProvisionMessage(null), 10000);
    }

    setProvisioning(false);
  };

  const handleGenerateQR = async (instanceName?: string) => {
    const name = instanceName || evolution.instance_name || instances[0]?.instance_name;
    if (!name) return;

    setReconnecting(true);
    setTestResult(null);
    setProvisionQrCode(null);
    setReconnectQrCode(null);

    try {
      // Load instance credentials from evolution_instances
      const { data: instData } = await supabase
        .from('evolution_instances')
        .select('server_url, api_key')
        .eq('company_id', profile?.company_id)
        .eq('instance_name', name)
        .maybeSingle();

      const serverUrl = instData?.server_url || evolution.server_url;
      const apiKey = instData?.api_key || evolution.api_key;

      if (!serverUrl || !apiKey) {
        setTestResult({ success: false, message: 'Dados da instância não encontrados.' });
        setReconnecting(false);
        return;
      }

      const baseUrl = serverUrl.replace(/\/+$/, '');

      // First check if already connected
      const stateRes = await fetch(`${baseUrl}/instance/connectionState/${name}`, {
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
        },
      });

      if (stateRes.ok) {
        const stateData = await stateRes.json();
        if (stateData?.instance?.state === 'open') {
          // Already connected! Update DB and show success
          await supabase
            .from('evolution_instances')
            .update({ status: 'connected', qrcode_base64: null, updated_at: new Date().toISOString() })
            .eq('company_id', profile?.company_id)
            .eq('instance_name', name);
          await supabase
            .from('whatsapp_configs')
            .update({ is_connected: true, updated_at: new Date().toISOString() })
            .eq('company_id', profile?.company_id);
          await fetchInstances();
          setTestResult({ success: true, message: 'WhatsApp já está conectado e funcionando!' });
          setIsConnected(true);
          setReconnecting(false);
          return;
        }

        // If state is 'close'/'closed', try to restart instance first to restore session
        if (stateData?.instance?.state === 'close' || stateData?.instance?.state === 'closed') {
          setTestResult({ success: true, message: 'Conexão caiu. Tentando restaurar sessão automaticamente...' });
          try {
            const restartRes = await fetch(`${baseUrl}/instance/restart/${name}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                apikey: apiKey,
              },
            });

            if (restartRes.ok) {
              // Session restored! Update DB
              await supabase
                .from('evolution_instances')
                .update({ status: 'connecting', updated_at: new Date().toISOString() })
                .eq('company_id', profile?.company_id)
                .eq('instance_name', name);
              await fetchInstances();
              setTestResult({ success: true, message: 'Sessão restaurada! O WhatsApp deve reconectar em instantes. Se não conectar em 10s, gere o QR Code novamente.' });
              setReconnecting(false);
              return;
            }
          } catch {
            // restart failed — fall through to QR code generation
          }
        }
      }

      // Not connected and restart didn't work — generate QR code
      setTestResult({ success: true, message: 'Gerando novo QR Code...' });
      
      const connectUrl = `${baseUrl}/instance/connect/${name}`;

      const res = await fetch(connectUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          apikey: apiKey,
        },
      });

      const data = await res.json();

      if (res.ok) {
        const rawQr = data?.qrcode?.base64 || data?.base64 || null;
        if (rawQr) {
          // Strip "data:image/png;base64," prefix if already included
          const qr = rawQr.startsWith('data:') ? rawQr : `data:image/png;base64,${rawQr}`;
          setProvisionQrCode(qr);
          // Also save QR to evolution_instances
          await supabase
            .from('evolution_instances')
            .update({ status: 'qr_ready', qrcode_base64: qr, updated_at: new Date().toISOString() })
            .eq('company_id', profile?.company_id)
            .eq('instance_name', name);
          await fetchInstances();
          setTestResult(null);
        } else {
          setTestResult({
            success: false,
            message: 'Instância não retornou QR Code. Verifique o status no servidor.',
          });
        }
      } else {
        setTestResult({
          success: false,
          message: `Erro ao gerar QR: ${data?.message || data?.error || 'Falha desconhecida'}`,
        });
      }
    } catch (err: unknown) {
      setTestResult({
        success: false,
        message: `Erro: ${err instanceof Error ? err.message : 'Erro inesperado'}`,
      });
    }

    setReconnecting(false);
  };

  const handleReconnect = async () => {
    await handleGenerateQR();
  };

  const handleDeleteInstance = async (instanceName: string) => {
    if (!window.confirm(`Tem certeza que deseja desconectar a instância "${instanceName}"?\n\nIsso irá:\n1. Desconectar o WhatsApp no servidor\n2. Remover a instância do servidor\n3. Limpar os dados locais`)) return;

    setDeletingInstance(instanceName);
    setTestResult(null);

    const evolutionResults: string[] = [];

    try {
      // Always fetch fresh instance data from DB — works for both SaaS-provisioned and manual instances
      const { data: instData } = await supabase
        .from('evolution_instances')
        .select('server_url, api_key')
        .eq('company_id', profile?.company_id)
        .eq('instance_name', instanceName)
        .maybeSingle();

      const serverUrl = instData?.server_url || evolution.server_url;
      const apiKey = instData?.api_key || evolution.api_key;

      if (serverUrl && apiKey) {
        const baseUrl = serverUrl.replace(/\/+$/, '');

        // Step 1: Logout (disconnect WhatsApp session) on Evolution server
        try {
          const logoutUrl = `${baseUrl}/instance/logout/${instanceName}`;
          const logoutRes = await fetch(logoutUrl, {
            method: 'DELETE',
            headers: { apikey: apiKey },
          });

          if (logoutRes.ok) {
            evolutionResults.push('WhatsApp desconectado no servidor');
          } else {
            const logoutBody = await logoutRes.text();
            evolutionResults.push(`Aviso: servidor respondeu ${logoutRes.status} ao desconectar — ${logoutBody || 'sem detalhes'}`);
          }
        } catch (logoutErr) {
          evolutionResults.push(`Aviso: não foi possível comunicar com o servidor para desconectar (${logoutErr instanceof Error ? logoutErr.message : 'erro de rede'})`);
        }

        // Step 2: Delete instance from Evolution server
        try {
          const deleteUrl = `${baseUrl}/instance/delete/${instanceName}`;
          const deleteRes = await fetch(deleteUrl, {
            method: 'DELETE',
            headers: { apikey: apiKey },
          });

          if (deleteRes.ok) {
            evolutionResults.push('Instância removida do servidor');
          } else {
            const deleteBody = await deleteRes.text();
            evolutionResults.push(`Aviso: servidor respondeu ${deleteRes.status} ao remover instância — ${deleteBody || 'sem detalhes'}`);
          }
        } catch (deleteErr) {
          evolutionResults.push(`Aviso: não foi possível remover instância do servidor (${deleteErr instanceof Error ? deleteErr.message : 'erro de rede'})`);
        }
      } else {
        evolutionResults.push('Aviso: sem credenciais do servidor — apenas limpeza local realizada');
      }

      // Step 3: Always clean up locally (user explicitly asked to disconnect)
      // Delete from Supabase by company_id + instance_name (more robust than ID)
      await supabase
        .from('evolution_instances')
        .delete()
        .eq('company_id', profile?.company_id)
        .eq('instance_name', instanceName);

      // Clear whatsapp_configs evolution fields
      await supabase
        .from('whatsapp_configs')
        .update({
          evolution_instance_name: null,
          evolution_server_url: null,
          evolution_api_key: null,
          is_connected: false,
          updated_at: new Date().toISOString(),
        })
        .eq('company_id', profile?.company_id);

      setEvolution({ server_url: '', api_key: '', instance_name: '' });
      setIsConnected(false);
      setProvisionQrCode(null);
      setReconnectQrCode(null);
      await fetchInstances();

      setTestResult({
        success: true,
        message: `Instância "${instanceName}" desconectada. ${evolutionResults.join('. ')}.`,
      });
    } catch (err) {
      setTestResult({
        success: false,
        message: `Erro ao remover: ${err instanceof Error ? err.message : 'Erro'}`,
      });
    }
    setDeletingInstance(null);
  };

  // ─── Send Test Message ───
  const currentInstanceName = evolution.instance_name || (instances.length > 0 ? instances[0].instance_name : '');
  const currentInstance = instances.find((i) => i.instance_name === currentInstanceName);

  const handleSendTest = async () => {
    if (!currentInstance || !testPhone.trim() || !testMessage.trim()) return;

    setSendingTest(true);
    setTestResult(null);

    try {
      const baseUrl = (currentInstance.server_url || evolution.server_url).replace(/\/+$/, '');
      const sendUrl = `${baseUrl}/message/sendText/${currentInstanceName}`;

      const res = await fetch(sendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: currentInstance.api_key || evolution.api_key,
        },
        body: JSON.stringify({
          number: testPhone.trim(),
          text: testMessage.trim(),
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setIsConnected(true);
        setTestResult({
          success: true,
          message: `Mensagem enviada com sucesso para ${testPhone.trim()}!`,
        });
        setTestMessage('Olá! Esta é uma mensagem de teste do WzapPro 🚀');
      } else {
        const apiErrorMsg = data?.message || data?.error || 'Falha desconhecida';
        setTestResult({
          success: false,
          message: `Erro ao enviar: ${apiErrorMsg}`,
        });
      }
    } catch (err: unknown) {
      setTestResult({
        success: false,
        message: `Erro: ${err instanceof Error ? err.message : 'Erro inesperado'}`,
      });
      setIsConnected(false);
    }

    setSendingTest(false);
  };

  // ─── Info cards ───
  const infoCards = provider === 'meta_cloud'
    ? [
        { icon: 'ri-guide-line', color: 'bg-primary-50 text-primary-600', title: 'Guia de Configuração', text: 'Acesse developers.facebook.com, crie um app Business e configure o produto WhatsApp.' },
        { icon: 'ri-webhook-line', color: 'bg-accent-50 text-accent-600', title: 'Webhook URL', text: 'Configure no Meta a URL do webhook para receber atualizações de status em tempo real.' },
        { icon: 'ri-shield-check-line', color: 'bg-green-50 text-green-600', title: 'Limites e Segurança', text: 'Contas empresariais têm limite de 250 msg/dia. Verificadas: 1000+. Token criptografado.' },
      ]
    : [
        { icon: 'ri-qr-code-line', color: 'bg-accent-50 text-accent-600', title: 'Conexão Simples', text: 'Clique em conectar, escaneie o QR Code com seu WhatsApp e pronto. Nenhuma configuração técnica necessária.' },
        { icon: 'ri-webhook-line', color: 'bg-primary-50 text-primary-600', title: 'Webhook de Status', text: 'Receba status de entrega e leitura em tempo real automaticamente, sem configuração manual.' },
        { icon: 'ri-lightbulb-flash-line', color: 'bg-green-50 text-green-600', title: 'Sem Limites da Meta', text: 'Usa WhatsApp Web por baixo. Ideal pra volumes maiores sem as restrições da Cloud API.' },
      ];

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-xl font-heading font-bold text-foreground-950">Configuração WhatsApp</h2>
        <p className="text-sm text-foreground-500 mt-1">
          {provider === 'meta_cloud'
            ? 'Configure sua conta oficial do WhatsApp Business API'
            : 'Conecte seu WhatsApp escaneando o QR Code — servidor gerenciado automaticamente'}
        </p>
      </div>

      {/* Provider Toggle */}
      <div className="inline-flex bg-background-100 rounded-full p-1">
        <button
          type="button"
          onClick={() => handleProviderSwitch('meta_cloud')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            provider === 'meta_cloud'
              ? 'bg-background-50 dark:bg-background-200 text-foreground-950 dark:text-foreground-100 shadow-sm'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-meta-line mr-1.5" />
          Meta Cloud API
        </button>
        <button
          type="button"
          onClick={() => handleProviderSwitch('evolution')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            provider === 'evolution'
              ? 'bg-background-50 dark:bg-background-200 text-foreground-950 dark:text-foreground-100 shadow-sm'
              : 'text-foreground-500 hover:text-foreground-700'
          }`}
        >
          <i className="ri-rocket-line mr-1.5" />
          Instância Própria
        </button>
      </div>

      {/* Status Banner */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${
        isConnected
          ? 'bg-green-50 border-green-200 text-green-800'
          : 'bg-amber-50 border-amber-200 text-amber-800'
      }`}>
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isConnected ? 'bg-green-500' : 'bg-amber-500'}`} />
        <span className="font-medium">
          {isConnected ? 'WhatsApp conectado e funcionando' : 'WhatsApp não configurado'}
        </span>
        <span className="text-foreground-500 ml-auto text-xs">
          Provedor: {provider === 'meta_cloud' ? 'Meta Cloud API' : 'Instância Própria'}
        </span>
        {!isConnected && provider === 'evolution' && currentInstanceName && instances.length > 0 && (
          <button
            onClick={handleReconnect}
            disabled={reconnecting}
            className="ml-3 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            {reconnecting ? (
              <><i className="ri-loader-4-line animate-spin" /> Gerando QR...</>
            ) : (
              <><i className="ri-qr-code-line" /> Reconectar</>
            )}
          </button>
        )}
      </div>

      {testResult && (
        <div className={`px-4 py-3 rounded-lg text-sm font-medium ${
          testResult.success
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center gap-2">
            <i className={`text-lg ${testResult.success ? 'ri-checkbox-circle-fill text-green-600' : 'ri-error-warning-fill text-red-500'}`} />
            {testResult.message}
          </div>
        </div>
      )}

      {/* QR Code para reconectar */}
      {reconnectQrCode && (
        <div className="bg-white rounded-2xl border-2 border-dashed border-amber-300 p-6 text-center">
          <p className="text-sm font-semibold text-foreground-900 mb-1">Escaneie o QR Code com o WhatsApp</p>
          <p className="text-xs text-foreground-500 mb-4">Abra o WhatsApp no celular, vá em Aparelhos Conectados e escaneie</p>
          <img
            src={reconnectQrCode || ''}
            alt="QR Code WhatsApp"
            className="w-52 h-52 mx-auto rounded-xl"
          />
          <button
            onClick={() => setReconnectQrCode(null)}
            className="mt-4 text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer underline"
          >
            Fechar QR Code
          </button>
        </div>
      )}

      {/* ─── META CLOUD API FORM ─── */}
      {provider === 'meta_cloud' && (
        <form onSubmit={handleSave} className="bg-background-100 rounded-xl border border-background-200 p-5 space-y-5">
          <h3 className="text-base font-heading font-semibold text-foreground-950">Credenciais Meta Cloud API</h3>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Phone Number ID</label>
            <input
              type="text"
              value={meta.phone_number_id}
              onChange={(e) => setMeta({ ...meta, phone_number_id: e.target.value })}
              placeholder="55119XXXXXXXX"
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all font-mono"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Access Token</label>
            <div className="relative">
              <input
                type={showMetaToken ? 'text' : 'password'}
                value={meta.access_token}
                onChange={(e) => setMeta({ ...meta, access_token: e.target.value })}
                placeholder="EAA..."
                className="w-full px-3 py-2.5 pr-10 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all font-mono"
              />
              <button type="button" onClick={() => setShowMetaToken(!showMetaToken)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer">
                <i className={`text-lg ${showMetaToken ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Business Account ID</label>
              <input
                type="text"
                value={meta.business_account_id}
                onChange={(e) => setMeta({ ...meta, business_account_id: e.target.value })}
                placeholder="123456789..."
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Versão da API</label>
              <select
                value={meta.api_version}
                onChange={(e) => setMeta({ ...meta, api_version: e.target.value })}
                className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
              >
                <option value="v22.0">v22.0 (mais recente)</option>
                <option value="v21.0">v21.0</option>
                <option value="v20.0">v20.0</option>
                <option value="v19.0">v19.0</option>
                <option value="v18.0">v18.0</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Webhook Verify Token</label>
            <input
              type="text"
              value={meta.webhook_verify_token}
              onChange={(e) => setMeta({ ...meta, webhook_verify_token: e.target.value })}
              placeholder="meu_token_secreto_123"
              className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all font-mono"
            />
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-background-100">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || !isValid}
              className="px-4 py-2 text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testing ? <><i className="ri-loader-4-line animate-spin text-lg" />Testando...</> : <><i className="ri-plug-line text-lg" />Testar Conexão</>}
            </button>
            <div className="flex items-center gap-3">
              {saved && <span className="text-sm text-green-600 font-medium flex items-center gap-1"><i className="ri-check-line" />Salvo!</span>}
              <button type="submit" disabled={saving} className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50">
                {saving ? <><i className="ri-loader-4-line animate-spin text-lg" />Salvando...</> : <><i className="ri-save-line text-lg" />Salvar Configurações</>}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* ─── EVOLUTION SAAS CONNECTION ─── */}
      {provider === 'evolution' && (
        <>
          {/* Not configured — show provision card */}
          {(!evolution.instance_name && instances.length === 0) ? (
            <div className="bg-white rounded-2xl border border-background-200 p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-whatsapp-line text-3xl text-primary-500" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                Conecte seu WhatsApp
              </h3>
              <p className="text-sm text-foreground-500 max-w-sm mx-auto mb-6">
                Clique no botão abaixo para criar sua instância automaticamente no servidor do sistema. Depois é só escanear o QR Code.
              </p>
              <button
                onClick={handleProvision}
                disabled={provisioning}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                {provisioning ? (
                  <><i className="ri-loader-4-line animate-spin" /> Criando instância...</>
                ) : (
                  <><i className="ri-qr-code-line" /> Conectar WhatsApp</>
                )}
              </button>
              <p className="text-xs text-foreground-400 mt-3">
                Não precisa configurar servidor. Levamos menos de 10 segundos.
              </p>
            </div>
          ) : (
            <>
              {/* Status cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-background-100 rounded-xl border border-background-200 p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-foreground-500">Status</p>
                    {(instances.length > 0 || !!currentInstanceName) && (
                      <button
                        onClick={() => handleDeleteInstance(instances[0]?.instance_name || currentInstanceName)}
                        disabled={deletingInstance === (instances[0]?.instance_name || currentInstanceName)}
                        className="text-[11px] text-red-500 hover:text-red-600 hover:bg-red-50 px-2 py-0.5 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                        title="Desconectar WhatsApp"
                      >
                        {deletingInstance === (instances[0]?.instance_name || currentInstanceName) ? (
                          <i className="ri-loader-4-line animate-spin" />
                        ) : (
                          <i className="ri-close-circle-line" />
                        )}
                        Desconectar
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                    <span className={`text-sm font-semibold ${isConnected ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {isConnected ? 'Conectado' : 'Desconectado'}
                    </span>
                  </div>
                  {!isConnected && (
                    <button
                      onClick={handleReconnect}
                      disabled={reconnecting}
                      className="mt-2 w-full px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                    >
                      {reconnecting ? (
                        <><i className="ri-loader-4-line animate-spin" /> Gerando QR...</>
                      ) : (
                        <><i className="ri-qr-code-line" /> Gerar QR Code</>
                      )}
                    </button>
                  )}
                </div>
                <div className="bg-background-100 rounded-xl border border-background-200 p-4">
                  <p className="text-xs text-foreground-500 mb-1">Instância</p>
                  <p className="text-sm font-semibold text-foreground-900">{currentInstanceName || '—'}</p>
                </div>
              </div>

              {/* QR Code */}
              {(provisionQrCode || reconnectQrCode) && (
                <div className="bg-white rounded-2xl border-2 border-dashed border-amber-300 p-6 text-center">
                  <p className="text-sm font-semibold text-foreground-900 mb-1">Escaneie o QR Code com o WhatsApp</p>
                  <p className="text-xs text-foreground-500 mb-4">Abra o WhatsApp no celular, vá em Aparelhos Conectados e escaneie</p>
                  <img
                    src={provisionQrCode || reconnectQrCode || ''}
                    alt="QR Code WhatsApp"
                    className="w-52 h-52 mx-auto rounded-xl"
                  />
                  <button
                    onClick={() => { setProvisionQrCode(null); setReconnectQrCode(null); }}
                    className="mt-4 text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer underline"
                  >
                    Fechar QR Code
                  </button>
                </div>
              )}

              {/* Test message */}
              <div className="bg-background-50 rounded-xl border border-background-200 p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-heading font-semibold text-foreground-950">Enviar Mensagem de Teste</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">Teste o envio real para confirmar que está funcionando</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Número do destinatário</label>
                    <input
                      type="text"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="5511999999999"
                      className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all font-mono"
                    />
                    <p className="text-[10px] text-foreground-400 mt-1">DDI + DDD + número, sem espaços</p>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handleSendTest}
                      disabled={sendingTest || !testPhone.trim() || !currentInstanceName}
                      className="w-full px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {sendingTest ? (
                        <><i className="ri-loader-4-line animate-spin text-lg" /> Enviando...</>
                      ) : (
                        <><i className="ri-send-plane-fill text-lg" /> Enviar mensagem de teste</>
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Mensagem</label>
                  <textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all resize-none"
                    placeholder="Digite a mensagem de teste..."
                  />
                  <p className="text-[10px] text-foreground-400 mt-1">{testMessage.length} caracteres</p>
                </div>
              </div>
            </>
          )}

          {provisionMessage && (
            <div className={`px-4 py-3 rounded-lg text-sm font-medium ${
              provisionMessage.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <div className="flex items-center gap-2">
                <i className={`text-lg ${provisionMessage.type === 'success' ? 'ri-checkbox-circle-fill text-green-600' : 'ri-error-warning-fill text-red-500'}`} />
                {provisionMessage.text}
              </div>
            </div>
          )}
        </>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {infoCards.map((card, i) => (
          <div key={i} className="bg-background-100 rounded-xl border border-background-200 p-4">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${card.color.split(' ')[0]}`}>
              <i className={`${card.icon} ${card.color.split(' ')[1]} text-lg`} />
            </div>
            <h4 className="text-sm font-semibold text-foreground-900 mb-1">{card.title}</h4>
            <p className="text-xs text-foreground-500 leading-relaxed">{card.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}